
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import Navigation from "./Navigation";
import DocLicenseNoModal from "../doc/DocLicenseNoModal";
import config from "../../config";
import PartnerApi from "../../helpers/PartnerApi";
import LoadingIcon from "../../../src/icons-svg/icon_loading.svg";

const MainLayout = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true); 
  const [loading, setLoading] = useState(false);
  const [selectedLicenseNo, setSelectedLicenseNo] = useState("");
  const [docList, setDocList] = useState([]);
  const [docInfo, setDocInfo] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [language, setLanguage] = useState("tr");

  const fetchDocs = async (licenseNo, lang, link) => {
    if (!licenseNo) return;
    setLoading(true);
    try {
      // Menü için docList al
      const res = await PartnerApi.getlicenseDocList({ licenseNo, language: lang });
      setDocList(res?.docSearchList || []);

      // Link parametresi varsa seçili dokümanı getir
      let infoRes = null;
      if (link) {
        infoRes = await PartnerApi.getlicenseDocInfo({
          licenseNo,
          language: lang,
          helpPath: link,
        });
      }

      // Eğer link geçerli değilse selectedDoc null, docInfo sadece genel bilgiyi al
      if (!infoRes?.licenseDocInfo?.docUuid) {
        setSelectedDoc(null);

        // docInfo için lisans bilgilerini al
        if (res?.docSearchList?.length > 0) {
          const firstGroup = res.docSearchList[0];
          const firstDoc = firstGroup?.docList?.[0];
          if (firstDoc) {
            const info = await PartnerApi.getlicenseDocInfo({
              licenseNo,
              language: lang,
              helpPath: firstDoc.description?.subject.replace(/\s+/g, "_"),
            });
            setDocInfo(info);
          }
        }
      } else {
        // Link geçerli ise dokümanı seç
        setSelectedDoc({
          uuid: infoRes.licenseDocInfo.docUuid,
          title: link,
          helpPath: link,
        });
        setDocInfo(infoRes);
      }
    } catch (error) {
      console.error("Doküman yüklenirken hata:", error);
      setDocList([]);
      setDocInfo(null);
      setSelectedDoc(null);
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  // URL parametrelerini kontrol et
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlLicenseNo = searchParams.get("licenseNo") || "";
    const urlLanguage = searchParams.get("language") || "tr";
    const urlLink = searchParams.get("link") || "";

    setLanguage(urlLanguage);
    i18n.changeLanguage(urlLanguage);
    setSelectedLicenseNo(urlLicenseNo);

    if (!urlLicenseNo) {
      setIsLicenseModalOpen(true);
      setDocList([]);
      setDocInfo(null);
      setSelectedDoc(null);
      setIsLoading(false);
      return;
    }

    setIsLicenseModalOpen(false);
    fetchDocs(urlLicenseNo, urlLanguage, urlLink);

    window.history.replaceState({}, document.title, location.pathname);
  }, [i18n, location.search]);

  const handleDocSelect = ({ uuid, title, helpPath }) => {
    setSelectedDoc({ uuid, title, helpPath });
  };

  const handleModalSubmit = ({ licenseNo }) => {
    if (!licenseNo) return;

    const cleanLicenseNo = licenseNo.trim().replace(/\s+/g, "");
    setSelectedLicenseNo(cleanLicenseNo);
    window.history.replaceState({}, document.title, location.pathname);

    fetchDocs(cleanLicenseNo, language);
    setIsLicenseModalOpen(false);
  };

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
    window.history.replaceState({}, document.title, location.pathname);
    fetchDocs(selectedLicenseNo, newLang, selectedDoc?.helpPath || "");
  };

  return (
    <>
      <div className="flex flex-col h-screen bg-white dark:bg-black overflow-hidden">
        <Header
          selectedDoc={selectedDoc}
          language={language}
          onLanguageChange={handleLanguageChange}
          licenseNo={selectedLicenseNo}
          docInfo={docInfo}
        />

        <div className="flex flex-1 pt-14 overflow-hidden">
          <Navigation
            docList={docList}
            docInfo={docInfo}
            onDocSelect={handleDocSelect}
            openLicenseModal={() => setIsLicenseModalOpen(true)}
            language={language}
          />

          <main className="flex-1 relative">
            {selectedDoc?.uuid ? (
              <iframe
                key={selectedDoc.uuid}
                title={selectedDoc.title}
                src={`${config().HELP_URL}/displayDoc?docUuid=${selectedDoc.uuid}&language=${language}`}
                className="w-full h-full border-none"
              />
            ) : loading ? (
              <div className="flex flex-col items-center justify-center h-full text-sm font-roboto px-4 text-center">
                <img src={LoadingIcon} className="w-8 h-8 mb-2" alt="Loading" />
                  <span>{t("loadingData")}</span>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">
               <div>{t("noDocumentSelected")}</div> 
              </div>
            )}
          </main>
        </div>
      </div>

      <DocLicenseNoModal
        visible={isLicenseModalOpen}
        onClose={() => setIsLicenseModalOpen(false)}
        onSubmit={handleModalSubmit}
        showLinkField={false}
      />
    </>
  );
};

export default MainLayout;















